import type { ColumnDef } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { BoolCell } from '@/components/data-table/cells';
import type { Jurisdiction } from '@/services/api/jurisdictions';

export type JurisdictionFilterType = 'multi-text' | 'multi-choice';

export interface JurisdictionFilterOption {
  label: string;
  value: string | boolean;
}

export interface JurisdictionColumnConfig {
  key: keyof Jurisdiction;
  title: string;
  sortable: boolean;
  filterable: boolean;
  filterType?: JurisdictionFilterType;
  /** Static options; omitted → derived from distinct data values (arrays flattened, sorted) */
  filterOptions?: JurisdictionFilterOption[];
}

const BOOLEAN_FILTER_OPTIONS: JurisdictionFilterOption[] = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

// Legacy column config. The three boolean columns were
// `multi-text` in legacy, but text filters never match boolean cell values (exact
// `includes` against strings) so those filters were dead — ported as `multi-choice`
// boolean filters instead (deliberate fix).
export const JURISDICTION_COLUMNS: JurisdictionColumnConfig[] = [
  { key: 'flagUrl', title: '', sortable: false, filterable: false },
  { key: 'name', title: 'Jurisdiction', sortable: true, filterable: true, filterType: 'multi-text' },
  { key: 'code', title: 'Alpha 2 Code', sortable: true, filterable: true, filterType: 'multi-text' },
  { key: 'alpha3', title: 'Alpha 3 Code', sortable: true, filterable: true, filterType: 'multi-text' },
  { key: 'amlRisk', title: 'Risk Level', sortable: true, filterable: true, filterType: 'multi-choice' },
  { key: 'phoneCode', title: 'Phone Code', sortable: true, filterable: true, filterType: 'multi-text' },
  { key: 'name_de', title: 'German Name', sortable: false, filterable: false },
  { key: 'requiredDocumentIds', title: 'Required Document IDs', sortable: false, filterable: true, filterType: 'multi-choice' },
  { key: 'allowedResidency', title: 'Residency Allowed', sortable: true, filterable: true, filterType: 'multi-choice', filterOptions: BOOLEAN_FILTER_OPTIONS },
  { key: 'allowedCitizenship', title: 'Citizenship Allowed', sortable: true, filterable: true, filterType: 'multi-choice', filterOptions: BOOLEAN_FILTER_OPTIONS },
  { key: 'allowedFiatTransfers', title: 'FIAT Transfers Allowed', sortable: true, filterable: true, filterType: 'multi-choice', filterOptions: BOOLEAN_FILTER_OPTIONS },
  { key: 'eea', title: 'EEA', sortable: true, filterable: true, filterType: 'multi-choice', filterOptions: BOOLEAN_FILTER_OPTIONS },
  { key: 'eu', title: 'EU', sortable: true, filterable: true, filterType: 'multi-choice', filterOptions: BOOLEAN_FILTER_OPTIONS },
  { key: 'postalCodeUse', title: 'Postal Code Use', sortable: true, filterable: true, filterType: 'multi-choice' },
  { key: 'allowedProductsRetail', title: 'Retail Products', sortable: false, filterable: true, filterType: 'multi-choice' },
  { key: 'allowedProductsProfessional', title: 'Professional Products', sortable: false, filterable: true, filterType: 'multi-choice' },
];

const FLAG_WIDTH_PX = 22;

const FlagCell = ({ name, url }: { name: string; url: string }) => <img alt={`${name} flag`} className="rounded-xs" loading="lazy" src={url} width={FLAG_WIDTH_PX} />;
FlagCell.displayName = 'FlagCell';

const renderCell = (key: keyof Jurisdiction, row: Jurisdiction): ReactNode => {
  if (key === 'flagUrl') return <FlagCell name={row.name} url={row.flagUrl} />;
  const value = row[key];
  if (typeof value === 'boolean') return <BoolCell value={value} />;
  if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
  return value;
};

export const buildJurisdictionColumns = (): ColumnDef<Jurisdiction, unknown>[] =>
  JURISDICTION_COLUMNS.map((config) => ({
    accessorKey: config.key,
    header: config.title,
    enableSorting: config.sortable,
    cell: ({ row }) => renderCell(config.key, row.original),
  }));
